import { lock, unlock } from "tua-body-scroll-lock";
import { isMobile } from "@helpers/utils";
import SearchAPIQueryHandler from "@helpers/search/query";
import { SearchAPIConfig, SearchAPISearchResults } from "@helpers/search/types";

class SearchInput extends HTMLElement {
  private searchInput!: HTMLInputElement | null;

  /*
    [TODO] Rename all these types to better imply what API they belong to.
  */

  // Boost API configuration
  //
  // public searchHandler: IBoostAPIQueryHandler = new BoostAPIQueryHandler();
  // private searchConfig: Partial<SearchParams | InstantSearchParams> = {};
  // private apiConfig: APIConfig = { action: "search" };
  //
  // End Boost API configuration

  // Search API configuration
  private searchHandler: SearchAPIQueryHandler;
  private apiConfig: SearchAPIConfig;
  // End Search API configuration
  private searchButton: HTMLButtonElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private viewAllButton: HTMLButtonElement | null = null;
  private waitingResults: HTMLElement | null = null;
  private hasResults: HTMLElement | null = null;
  private noResults: HTMLElement | null = null;
  private resultsParent!: HTMLElement | null;
  private suggestionColumn: HTMLElement | null = null;
  private collectionColumn: HTMLElement | null = null;
  private productColumn: HTMLElement | null = null;
  private articleColumn: HTMLElement | null = null;
  private overlay: HTMLElement | null = null;
  private headerSearchButton: HTMLButtonElement | null = null;

  constructor() {
    super();
    // Boost API configuration
    //
    // this.searchConfig = {
    //   scope: "global",
    //   product_available: true,
    //   variant_available: true,
    //   page_limit: 4,
    //   collection_limit: 4,
    //   product_limit: 4,
    //   suggestion_limit: 4,
    // };
    //
    // this.apiConfig = {
    //   action: "search/suggest",
    //   defaults: this.searchConfig,
    // };
    //
    // End Boost API configuration

    // Search API configuration
    this.apiConfig = {
      SearchAPISearchOptions: {
        resources: ["product"],
        unavailable_products: "hide",
        fields: ["title", "product_type", "variants.title", "vendor"],
        limit: 4,
      },
    };
    this.searchHandler = new SearchAPIQueryHandler(this.apiConfig);
    // End Search API configuration
  }

  connectedCallback() {
    this.cacheDomElements();
    this.setEventListeners();
    this.setInitialValueFromUrl();
  }

  cacheDomElements() {
    this.searchInput = this.querySelector("[js-search-input='input']");

    if (!this.searchInput) {
      throw new Error("search input is a required element");
    }

    this.searchButton = this.querySelector("[js-search-input='button']");
    this.closeButton = this.querySelector("[js-search-input='icon-close']");
    this.viewAllButton = this.querySelector("[js-instant-search='view-all']");
    this.resultsParent = this.querySelector(
      "[js-instant-search='results-parent']",
    );
    this.waitingResults = this.querySelector(
      '[js-instant-search="waiting-input"]',
    );
    this.hasResults = this.querySelector('[js-instant-search="has-results"]');
    this.noResults = this.querySelector('[js-instant-search="no-results"]');
    this.suggestionColumn = this.querySelector(
      "[js-instant-search='suggestions']",
    );
    this.collectionColumn = this.querySelector(
      "[js-instant-search='collections']",
    );
    this.productColumn = this.querySelector("[js-instant-search='products']");
    this.articleColumn = this.querySelector("[js-instant-search='articles']");
    this.overlay = this.querySelector("[js-instant-search='overlay']");
    this.headerSearchButton = document.querySelector(
      "[js-header='mobile-search']",
    );

    // Set initial overlay margin class based on header state
    this.updateOverlayMarginClass();
  }

  updateOverlayMarginClass() {
    const header = document.querySelector(
      ".header-background, .header-background-transparent",
    );
    if (!this.overlay || !header) return;
    this.overlay.classList.remove(
      "instant-search-results__overlay--no-margin",
      "instant-search-results__overlay--negative-margin",
    );
    if (header.classList.contains("header-background")) {
      this.overlay.classList.add("instant-search-results__overlay--no-margin");
    } else {
      this.overlay.classList.add(
        "instant-search-results__overlay--negative-margin",
      );
    }
  }

  setEventListeners() {
    this.searchInput?.addEventListener("input", () => this.handleSearchInput());

    this.searchInput?.addEventListener("focusin", () => {
      // Close any other open modals/drawers before opening search
      const openModals = document.querySelectorAll(
        ".modal.is-open, .drawer.is-open, .desktop-menu__item.is-open",
      );
      openModals.forEach((modal) => {
        if (modal.classList.contains("desktop-menu__item")) {
          modal.classList.remove("is-open");
          const desktopMenu = modal.closest("uwp-desktop-menu") as any;
          if (desktopMenu?.closeAllDropdowns) {
            desktopMenu.closeAllDropdowns();
          }
        } else if (
          modal.classList.contains("modal") ||
          modal.classList.contains("drawer")
        ) {
          const hideMethod =
            (modal as any).hideModal || (modal as any).hideDrawer;
          if (hideMethod) {
            hideMethod.call(modal);
          }
        }
      });

      this.resultsParent?.classList.remove("visually-hidden");

      const value = this.searchInput?.value || "";
      this.getSearchResults(value);

      this.overlay?.classList.add("is-visible");
      this.updateOverlayMarginClass();

      // Add search-active class to body when search is active
      document.body.classList.add("search-active");

      if (isMobile()) {
        this.classList.add("is-active");
      } else {
        lock(this);
      }

      document.dispatchEvent(new CustomEvent("predictive-search:visible"));
    });

    this.addEventListener("focusout", (event) => {
      const target = event.relatedTarget as Node;

      if (!target) {
        return;
      }

      const isFocusingInsideParent =
        this.resultsParent?.contains(target) || this.contains(target);

      if (isFocusingInsideParent) {
        return;
      }

      this.resultsParent?.classList.add("visually-hidden");

      if (this.classList.contains("search-input--desktop")) {
        unlock(this);
      }

      this.overlay?.classList.remove("is-visible");
      this.classList.remove("is-active");

      // Remove search-active class from body when search is deactivated
      document.body.classList.remove("search-active");
    });

    this.searchButton?.addEventListener("click", (clickEvent) => {
      if (!this.searchInput || !this.searchInput.value) {
        clickEvent.preventDefault();
        return;
      }
    });

    this.closeButton?.addEventListener("click", () => {
      this.clearInput();

      // Remove search-active class from body when search is closed
      document.body.classList.remove("search-active");
    });

    this.viewAllButton?.addEventListener("click", () => {
      const query = this.searchInput?.value || "";
      window.location.href = `${window.Shopify.routes.root}search?type=product&q=${query}`;
    });

    document.addEventListener("click", (event) => {
      const clickedElement = event.target as Node;

      let isClickOutside =
        !this.contains(clickedElement) ||
        this.overlay?.contains(clickedElement);

      if (clickedElement === this.headerSearchButton) {
        isClickOutside = false;
      }

      if (isClickOutside && this.overlay?.classList.contains("is-visible")) {
        this.resultsParent?.classList.add("visually-hidden");

        if (this.classList.contains("search-input--desktop")) {
          unlock(this);
        }

        this.overlay?.classList.remove("is-visible");
        this.classList.remove("is-active");

        // Remove search-active class from body when clicking outside search
        document.body.classList.remove("search-active");
      }
    });

    this.headerSearchButton?.addEventListener("click", () => {
      if (!this.classList.contains("search-input--mobile")) {
        return;
      }

      this.searchInput?.focus();

      // Add search-active class to body when search button is clicked
      document.body.classList.add("search-active");
    });
  }

  /**
   * Renders the search results panel based on the query and results received.
   * It updates the visibility of result sections such as "waiting", "has
   * results", and "no results".
   *
   * @param {string|undefined} query - The search query.
   * @param {BoostInstantSearchResponse|undefined} results - The search results object.
   */
  renderResultsPanel(
    query: string | undefined,
    results?: SearchAPISearchResults | undefined,
  ) {
    if (!query || !results) {
      this.waitingResults?.classList.remove("visually-hidden");
      this.hasResults?.classList.add("visually-hidden");
      this.noResults?.classList.add("visually-hidden");
      return;
    }

    if (query && !results.all_empty) {
      this.waitingResults?.classList.add("visually-hidden");
      this.hasResults?.classList.remove("visually-hidden");
      this.noResults?.classList.add("visually-hidden");
    } else if (query && results.all_empty) {
      this.waitingResults?.classList.remove("visually-hidden");
      this.hasResults?.classList.add("visually-hidden");
      this.noResults?.classList.remove("visually-hidden");
    }
  }

  changeButtonState() {
    if (!this.searchButton) {
      return;
    }
  }

  clearInput() {
    if (!this.searchInput) {
      return;
    }

    this.searchInput.value = "";
    this.handleSearchInput();
  }

  /**
   * Retrieves search results based on the given query.
   * It directly interacts with the SearchAPIQueryHandler to fetch data and
   * renders the results using `renderResultsPanel`.
   *
   * @param {string} query - The search query string to be used to fetch search results.
   */
  async getSearchResults(query: string) {
    if (!query.trim()) {
      this.renderResultsPanel("");
      return;
    }

    try {
      const results = (await this.searchHandler.executeSearch(
        query,
      )) as SearchAPISearchResults;

      this.renderResultsPanel(query, results);
      this.renderSearchResults(results);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error);
    }
  }

  /**
   * Helper function to determine if a given results object is an instance of
   * SearchResults.
   * This method uses type guarding to check if the 'products' property is
   * present.
   *
   * @param {SearchAPISearchResults} results - The
   * results object to check.
   * @returns {boolean} True if the results object is a SearchResults, false
   * otherwise.
   */

  isSearchApiResponse(
    results: SearchAPISearchResults,
  ): results is SearchAPISearchResults {
    return (results as SearchAPISearchResults).products !== undefined;
  }

  /**
   * Renders search results by creating and appending result elements to their respective columns.
   * It delegates to other methods for creating individual result elements.
   *
   *  results - The results to render, either instant or detailed search responses.
   */
  renderSearchResults(results: SearchAPISearchResults) {
    this.clearExistingSearchResults();
    const createResultAnchor = (href: string, text: string) => {
      const element = document.createElement("a");
      element.classList.add("instant-search-results__result", "text-sm");
      element.href = href;
      element.textContent = text;
      return element;
    };

    const appendResults = (
      container: HTMLElement,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any[],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      createElement: (dataItem: any) => HTMLElement,
    ) => {
      if (data.length) {
        container.classList.remove("visually-hidden");
        const resultsContainer = container.querySelector(
          ".instant-search-results__results",
        ) as HTMLElement;
        data.forEach((item) =>
          resultsContainer.appendChild(createElement(item)),
        );
      } else {
        container.classList.add("visually-hidden");
      }
    };
    if (this.isSearchApiResponse(results)) {
      if (this.productColumn) {
        appendResults(this.productColumn, results.products, (product) => {
          const anchor = createResultAnchor(
            `${window.Shopify.routes.root}products/${product.handle}`,
            "",
          );
          anchor.classList.add("instant-search-results__product");
          if (product.featured_image) {
            const img = document.createElement("img");
            img.src = product.featured_image.url;
            img.alt = product.title;
            img.width = 80;
            img.height = 80;
            img.classList.add("instant-search-results__product-image");
            anchor.appendChild(img);
          }
          const productMeta = document.createElement("div");
          productMeta.classList.add("instant-search-results__product-meta");
          productMeta.innerHTML = `
            <p class="
              instant-search-results__product-title
              heading-4
            ">
              ${product.title}
            </p>
  
            <p class="
              instant-search-results__product-price
              text-md-body
            ">
              ${window.themeVars.store.currency.symbol}${product.price}
            </p>
          `;
          anchor.appendChild(productMeta);
          return anchor;
        });
      }
    }
  }

  /**
   * Clears all existing search results from the DOM.
   * This method is typically called before rendering new search results to remove old ones.
   */
  clearExistingSearchResults() {
    if (this.suggestionColumn) {
      const suggestions = this.suggestionColumn.querySelector(
        ".instant-search-results__results",
      ) as HTMLElement;
      suggestions.innerHTML = "";
    }

    if (this.collectionColumn) {
      const collections = this.collectionColumn.querySelector(
        ".instant-search-results__results",
      ) as HTMLElement;
      collections.innerHTML = "";
    }

    if (this.productColumn) {
      const products = this.productColumn.querySelector(
        ".instant-search-results__results",
      ) as HTMLElement;
      products.innerHTML = "";
    }

    if (this.articleColumn) {
      const articles = this.articleColumn.querySelector(
        ".instant-search-results__results",
      ) as HTMLElement;
      articles.innerHTML = "";
    }
  }

  /**
   * Handles the input event on the search input field.
   * It synchronizes the search attribute across other inputs and updates the URL query.
   *
   */
  handleSearchInput() {
    const searchValue = this.searchInput?.value || "";

    this.getSearchResults(searchValue);

    if (searchValue) {
      this.closeButton?.classList.remove("is-hidden");
    } else {
      this.closeButton?.classList.add("is-hidden");
    }
  }

  /**
   * Sets the initial search value based on the URL query parameters.
   * This method is called during the `connectedCallback` to initialize the search input with a potential query value from the URL.
   */
  setInitialValueFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("q");

    if (searchParam) {
      this.setAttribute("query", searchParam);
    }
  }
}

customElements.define("uwp-search-input", SearchInput);
